'use client';

import {
  normalizeAppPathname,
  getInstantRouteTransitionKind,
  type InstantRouteTransitionKind,
} from '@/lib/instantRouteTransition';

export type PendingNav = {
  href: string;
  pathname: string;
  fromPathname: string;
  startedAt: number;
  kind: InstantRouteTransitionKind;
} | null;

let pending: PendingNav = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function subscribeInstantRouteNav(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getInstantRouteNavSnapshot(): PendingNav {
  return pending;
}

export function getInstantRouteNavServerSnapshot(): PendingNav {
  return null;
}

function resolveHref(href: string): URL | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(href, window.location.origin);
  } catch {
    return null;
  }
}

/** Call right before router.push / Link navigation that must feel instant. */
export function beginInstantRouteTransition(href: string) {
  if (typeof window === 'undefined') return;

  const url = resolveHref(href);
  if (!url) return;
  if (url.origin !== window.location.origin) return;

  const kind = getInstantRouteTransitionKind(url.pathname);
  if (!kind) return;

  const fromPathname = normalizeAppPathname(window.location.pathname);
  const pathname = normalizeAppPathname(url.pathname);

  // Same-path query changes (sort/filter) use UrunlerListingGate — skip overlay.
  if (pathname === fromPathname) return;

  pending = {
    href: `${pathname}${url.search}`,
    pathname,
    fromPathname,
    startedAt: Date.now(),
    kind,
  };
  emit();
}

export function clearInstantRouteTransition() {
  if (!pending) return;
  pending = null;
  emit();
}
